<template>
	<div class="newsletter-templates-wrapper" :class="{ 'loading-state': loading }">
		<AppCard v-if="newsletterTemplates.length > 0" :title="getString('newsletter_templates')">
			<template #body>
				<div class="templates-list">
					<NewsletterTemplateItem
						v-for="(template, index) in newsletterTemplates"
						:key="index"
						:template="template"
						@click="!loading && openTemplate(template)"
					/>
				</div>
			</template>
		</AppCard>

		<AppBottomSheet
			:active="showBottomSheet"
			:forceWidth="bottomSheetWidth"
			@toggling-popup="closeBottomSheet"
		>
			<div v-if="showBottomSheet && selectedTemplate">
				<ModalHeader
					:title="getString('fill_template_variables')"
					:button-text="getString('use_template')"
					:button-disabled="!isFormValid"
					@click-button="submitTemplate"
				/>

				<div class="template-form">
					<AppList
						v-if="listItems.length > 0"
						:items="listItems"
					>
						<template v-for="dateVar in dateVariables" :key="dateVar.id" #[`date-${dateVar.id}`]>
							<div class="list-item-date-row">
								<p class="list-item-title">{{ dateVar.name }}</p>
								<DatePicker
									v-model="variableValues[dateVar.id]"
									:mode="dateVar.type"
									:is24hr="true"
									color="teal"
									:is-dark="userStore.darkMode"
								>
									<template #default="{ togglePopover }">
										<AppTextInput
											class="date-input"
											@click="togglePopover()"
											:value="formatDateValue(dateVar)"
											readonly
										/>
									</template>
								</DatePicker>
							</div>
						</template>
					</AppList>
				</div>
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import NewsletterTemplateItem from "@/console-new/components/marketing/NewsletterTemplateItem.vue";
import {DatePicker} from "v-calendar";
import {useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";
import {newsletterTemplates} from "@/console-new/helpers/newsletterTemplates";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		return {
			getString: languageStore.getString,
			userStore
		};
	},
	name: "NewsletterTemplatesList",
	components: {
		AppCard,
		AppBottomSheet,
		AppList,
		AppTextInput,
		ModalHeader,
		NewsletterTemplateItem,
		DatePicker
	},
	mixins: [WindowWidth],
	props: {
		loading: {
			type: Boolean,
			default: false
		}
	},
	emits: ['generate-newsletter'],
	data() {
		return {
			newsletterTemplates,
			selectedTemplate: null,
			showBottomSheet: false,
			variableValues: {}
		};
	},
	computed: {
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		nonDateTimeVariables() {
			if (!this.selectedTemplate) return [];

			return this.selectedTemplate.variables
				.filter(v => v.type === 'string' || v.type === 'number' || v.type === 'link' || v.type === 'time')
				.map(v => ({
					title: v.name,
					textInput: v.type === 'string' || v.type === 'link' || v.type === 'time',
					numberInput: v.type === 'number',
					value: this.variableValues[v.id] || '',
					inputType: v.type === 'link' ? 'url' : v.type === 'time' ? 'time' : 'text',
					callbackText: (v.type === 'string' || v.type === 'link' || v.type === 'time') ? (value) => {
						this.variableValues[v.id] = value;
					} : undefined,
					callbackNumber: v.type === 'number' ? (value) => {
						this.variableValues[v.id] = value;
					} : undefined
				}));
		},
		dateVariables() {
			if (!this.selectedTemplate) return [];

			return this.selectedTemplate.variables
				.filter(v => v.type === 'date' || v.type === 'datetime');
		},
		listItems() {
			if (!this.selectedTemplate) return [];

			// Maintain original order from template variables
			return this.selectedTemplate.variables.map(v => {
				// For date/datetime, use custom slot with DatePicker
				if (v.type === 'date' || v.type === 'datetime') {
					return {
						title: v.name,
						customSlot: `date-${v.id}`
					};
				}

				// For everything else (string, number, link, time), use AppList built-in inputs
				const item = {
					title: v.name,
					textInput: v.type === 'string' || v.type === 'link' || v.type === 'time',
					numberInput: v.type === 'number',
					value: v.type === 'number' ? (this.variableValues[v.id] ?? null) : (this.variableValues[v.id] || ''),
					inputType: v.type === 'link' ? 'url' : v.type === 'time' ? 'time' : 'text',
					placeholder: v.placeholder || '',
					callbackText: (v.type === 'string' || v.type === 'link' || v.type === 'time') ? (value) => {
						this.variableValues[v.id] = value;
					} : undefined,
					callbackNumber: v.type === 'number' ? (value) => {
						this.variableValues[v.id] = value;
					} : undefined
				};

				// Add number-specific props if type is number
				if (v.type === 'number') {
					if (v.min !== undefined) item.min = v.min;
					if (v.max !== undefined) item.max = v.max;
					if (v.symbol) item.symbol = v.symbol;
					item.rightSymbol = true;
				}

				return item;
			});
		},
		isFormValid() {
			if (!this.selectedTemplate) return false;

			return this.selectedTemplate.variables.every(v => {
				const value = this.variableValues[v.id];
				if (v.type === 'number') {
					return value !== null && value !== undefined && value !== '';
				}
				if (v.type === 'link') {
					return value && value !== '' && this.isValidUrl(value);
				}
				return value && value !== '';
			});
		}
	},
	methods: {
		isValidUrl(url) {
			try {
				const urlObj = new URL(url);
				return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
			} catch (e) {
				return false;
			}
		},
		formatDateValue(dateVar) {
			const value = this.variableValues[dateVar.id];
			if (!value) return '';

			if (dateVar.type === 'date') {
				if (value instanceof Date) {
					return value.toLocaleDateString();
				}
			} else if (dateVar.type === 'datetime') {
				if (value instanceof Date) {
					return value.toLocaleString();
				}
			}
			return '';
		},
		openTemplate(template) {
			this.selectedTemplate = template;
			this.variableValues = {};
			this.showBottomSheet = true;
		},
		closeBottomSheet(isOpen) {
			if (!isOpen) {
				this.showBottomSheet = false;
				this.selectedTemplate = null;
				this.variableValues = {};
			}
		},
		submitTemplate() {
			if (!this.isFormValid) return;

			let filledPrompt = this.selectedTemplate.prompt;

			this.selectedTemplate.variables.forEach(v => {
				let value = this.variableValues[v.id];

				// Format date/datetime values (time is already a string from input type="time")
				if (v.type === 'date' && value instanceof Date) {
					value = value.toLocaleDateString();
				} else if (v.type === 'datetime' && value instanceof Date) {
					value = value.toLocaleString();
				}

				filledPrompt = filledPrompt.replace(new RegExp(`{{${v.id}}}`, 'g'), value);
			});

			this.$emit('generate-newsletter', filledPrompt);
			this.closeBottomSheet(false);
		}
	}
}
</script>

<style scoped lang="scss">
.newsletter-templates-wrapper {
	width: 100%;

	&.loading-state {
		opacity: 0.5;
		pointer-events: none;
		cursor: not-allowed;
	}
}

.templates-list {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 20px;
}

.template-form {
	margin-top: 20px;
}

.list-item-date-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	gap: 8px;
}

.list-item-title {
	font-size: 16px;
	color: var(--txt-0);
}

.date-input {
	width: 240px;
	cursor: pointer;
}

@media (max-width: 1250px) {
	.date-input {
		width: 190px;
	}
}

@media (max-width: 720px) {
	.templates-list {
		grid-template-columns: 1fr;
	}
}
</style>
